// @ts-nocheck
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  CameraIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  ExclamationCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  FingerPrintIcon,
  KeyIcon,
  ShieldCheckIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import Context from '../../context/Context';
import { fetchUser } from '../../context/forms/FormsAction';
import Breadcrumb from '../../components/Breadcrumb';
import { URL } from '../../service/Service';
import Spinner from '../../components/Spinner';
import TwoFactorSettings from '../../components/TwoFactorSettings';

const roleLabels = {
  ADMIN: 'Administrador',
  COMPANY: 'Empresa',
  USER: 'Utilizador',
};

const PasswordField = ({ id, label, value, onChange, visible, onToggle, autoComplete }) => (
  <div>
    <label htmlFor={id} className="mb-2 block text-sm font-semibold text-slate-800">
      {label}
    </label>
    <div className="relative">
      <input
        id={id}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        required
        className="block w-full rounded-xl border border-slate-300 bg-white px-4 py-3 pr-12 text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-100"
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-xl text-slate-500 transition hover:bg-slate-50 hover:text-slate-800"
        aria-label={visible ? `Ocultar ${label.toLowerCase()}` : `Mostrar ${label.toLowerCase()}`}
      >
        {visible ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
      </button>
    </div>
  </div>
);

const UserPage = () => {
  const { state, dispatch } = useContext(Context);
  const userInfo = state.user.data || {};
  const { loading } = state.user;
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [visibleFields, setVisibleFields] = useState({ current: false, next: false, repeat: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageFeedback, setImageFeedback] = useState('');
  const [credentialClientId, setCredentialClientId] = useState('');
  const [credentialClientSecret, setCredentialClientSecret] = useState('');
  const [credentialPassword, setCredentialPassword] = useState('');
  const [credentialsConfigured, setCredentialsConfigured] = useState(false);
  const [credentialsLoading, setCredentialsLoading] = useState(true);
  const [credentialsSubmitting, setCredentialsSubmitting] = useState(false);
  const [credentialsFeedback, setCredentialsFeedback] = useState(null);

  const isAdmin = userInfo.userRole === 'ADMIN';
  const pages = useMemo(() => [
    {
      name: isAdmin ? 'Dashboard' : 'Formulários',
      href: isAdmin ? '/admin/dashboard' : '/forms',
      current: false,
    },
    {
      name: 'A minha conta',
      href: isAdmin ? '/admin/user' : '/user',
      current: true,
    },
  ], [isAdmin]);

  useEffect(() => {
    fetchUser(`${URL}/api/users/user`, {}, dispatch);
  }, [dispatch]);

  useEffect(() => {
    const loadCredentials = async () => {
      try {
        const token = sessionStorage.getItem('jwt');
        const response = await fetch(`${URL}/api/users/eupago-credentials`, {
          headers: {
            Accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          const message = await response.text();
          throw new Error(message || 'Não foi possível carregar as credenciais.');
        }

        const credentials = await response.json();
        setCredentialClientId(credentials.clientId || '');
        setCredentialsConfigured(Boolean(credentials.configured));
      } catch (error) {
        setCredentialsFeedback({
          type: 'error',
          message: error?.message || 'Não foi possível carregar as credenciais da API Eupago.',
        });
      } finally {
        setCredentialsLoading(false);
      }
    };

    void loadCredentials();
  }, []);

  const loadProfileImage = useCallback(async () => {
    const token = sessionStorage.getItem('jwt');
    const response = await fetch(`${URL}/api/users/profile-picture`, {
      headers: {
        Accept: 'image/*',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      cache: 'no-store',
    });

    if (response.status === 404 || response.status === 422) {
      setProfileImageUrl('');
      return;
    }
    if (!response.ok) {
      throw new Error('Não foi possível carregar a fotografia.');
    }

    const imageBlob = await response.blob();
    setProfileImageUrl(globalThis.URL.createObjectURL(imageBlob));
  }, []);

  useEffect(() => {
    // Load the persisted image directly. Depending on a previously fetched DTO
    // flag could leave the avatar empty after refreshing or signing in again.
    loadProfileImage().catch(() => setImageFeedback('Não foi possível carregar a fotografia.'));
  }, [loadProfileImage]);

  useEffect(() => () => {
    if (profileImageUrl) globalThis.URL.revokeObjectURL(profileImageUrl);
  }, [profileImageUrl]);

  const toggleVisibility = (field) => {
    setVisibleFields((current) => ({ ...current, [field]: !current[field] }));
  };

  const handleProfileImageChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    setImageFeedback('');

    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/bmp', 'image/webp'].includes(file.type)) {
      setImageFeedback('Escolha uma imagem JPG, PNG, BMP ou WebP.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setImageFeedback('A fotografia não pode exceder 5 MB.');
      return;
    }

    setIsUploadingImage(true);
    try {
      const token = sessionStorage.getItem('jwt');
      const body = new FormData();
      body.append('file', file);
      const response = await fetch(`${URL}/api/users/profile-picture`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body,
      });
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Não foi possível enviar a fotografia.');
      }
      await loadProfileImage();
      window.dispatchEvent(new Event('profile-picture-updated'));
      setImageFeedback('Fotografia atualizada.');
    } catch (error) {
      setImageFeedback(error?.message || 'Não foi possível enviar a fotografia.');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFeedback(null);

    if (newPassword.length < 8) {
      setFeedback({ type: 'error', message: 'A nova palavra-passe deve ter pelo menos 8 caracteres.' });
      return;
    }

    if (newPassword !== repeatPassword) {
      setFeedback({ type: 'error', message: 'As novas palavras-passe não coincidem.' });
      return;
    }

    setIsSubmitting(true);
    try {
      const token = sessionStorage.getItem('jwt');
      const response = await fetch(`${URL}/api/users/changepassword`, {
        method: 'PATCH',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ oldPassword: currentPassword, newPassword }),
      });

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Não foi possível alterar a palavra-passe.');
      }

      setCurrentPassword('');
      setNewPassword('');
      setRepeatPassword('');
      setFeedback({ type: 'success', message: 'A palavra-passe foi alterada com sucesso.' });
    } catch (error) {
      setFeedback({
        type: 'error',
        message: error?.message || 'Não foi possível alterar a palavra-passe. Confirme a palavra-passe atual.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCredentialsSubmit = async (event) => {
    event.preventDefault();
    setCredentialsFeedback(null);
    setCredentialsSubmitting(true);

    try {
      const token = sessionStorage.getItem('jwt');
      const response = await fetch(`${URL}/api/users/eupago-credentials`, {
        method: 'PATCH',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          clientId: credentialClientId,
          clientSecret: credentialClientSecret,
          currentPassword: credentialPassword,
        }),
      });

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Não foi possível guardar as credenciais.');
      }

      const credentials = await response.json();
      setCredentialClientId(credentials.clientId || credentialClientId);
      setCredentialClientSecret('');
      setCredentialPassword('');
      setCredentialsConfigured(Boolean(credentials.configured));
      setCredentialsFeedback({
        type: 'success',
        message: 'As credenciais da API Eupago foram guardadas com segurança.',
      });
    } catch (error) {
      setCredentialsFeedback({
        type: 'error',
        message: error?.message || 'Não foi possível guardar as credenciais da API Eupago.',
      });
    } finally {
      setCredentialsSubmitting(false);
    }
  };

  if (loading) return <Spinner />;

  const roleLabel = roleLabels[userInfo.userRole] || 'Utilizador';

  return (
    <main className="min-h-[calc(100vh-5rem)] bg-gradient-to-br from-slate-50 via-white to-emerald-50/40 px-4 py-6 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <Breadcrumb pages={pages} />

        <header className="mb-8 mt-7">
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.22em] text-emerald-700">Conta e segurança</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">A minha conta</h1>
          <p className="mt-2 max-w-2xl text-slate-600">
            Consulte os dados da sua conta, mantenha o acesso protegido e gira as credenciais da API Eupago.
          </p>
        </header>

        <div className="grid items-start gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
          <aside className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="bg-gradient-to-br from-green-cl to-emerald-800 px-6 py-8 text-white">
              <div className="relative mb-5 h-24 w-24">
                <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-3xl bg-white/15 ring-1 ring-white/30">
                  {profileImageUrl ? (
                    <img src={profileImageUrl} alt="Fotografia de perfil" className="h-full w-full object-cover" />
                  ) : (
                    <UserCircleIcon className="h-16 w-16" aria-hidden="true" />
                  )}
                </div>
                <label
                  htmlFor="profile-picture-input"
                  className={`absolute -bottom-2 -right-2 flex h-10 w-10 items-center justify-center rounded-full bg-white text-emerald-800 shadow-lg ring-1 ring-slate-200 transition hover:scale-105 ${isUploadingImage ? 'cursor-wait opacity-70' : 'cursor-pointer'}`}
                  title="Alterar fotografia"
                >
                  {isUploadingImage ? (
                    <span className="h-5 w-5 animate-spin rounded-full border-2 border-emerald-700 border-t-transparent" />
                  ) : (
                    <CameraIcon className="h-5 w-5" />
                  )}
                </label>
                <input
                  id="profile-picture-input"
                  type="file"
                  accept="image/jpeg,image/png,image/bmp,image/webp"
                  className="sr-only"
                  disabled={isUploadingImage}
                  onChange={handleProfileImageChange}
                />
              </div>
              <p className="mb-4 text-xs text-emerald-50">
                Clique na câmara para alterar a fotografia. Máximo 5 MB.
              </p>
              {imageFeedback && (
                <p className="mb-4 rounded-xl bg-white/10 px-3 py-2 text-xs text-white" role="status">
                  {imageFeedback}
                </p>
              )}
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-100">Perfil</p>
              <h2 className="mt-1 text-2xl font-bold !text-white">{roleLabel}</h2>
            </div>

            <div className="space-y-5 p-6">
              <div className="flex items-start gap-3">
                <span className="rounded-xl bg-emerald-50 p-2 text-emerald-700"><EnvelopeIcon className="h-5 w-5" /></span>
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</p>
                  <p className="mt-1 break-words font-medium text-slate-900">{userInfo.userEmail || '—'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 border-t border-slate-100 pt-5">
                <span className="rounded-xl bg-emerald-50 p-2 text-emerald-700"><ShieldCheckIcon className="h-5 w-5" /></span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Tipo de conta</p>
                  <p className="mt-1 font-medium text-slate-900">{roleLabel}</p>
                </div>
              </div>
            </div>
          </aside>

          <div className="space-y-6">
            <TwoFactorSettings />
            <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-start gap-4 border-b border-slate-100 px-6 py-6 sm:px-8">
              <span className="rounded-2xl bg-emerald-50 p-3 text-emerald-700"><KeyIcon className="h-6 w-6" /></span>
              <div>
                <h2 className="text-2xl font-bold text-slate-950">Alterar palavra-passe</h2>
                <p className="mt-1 text-sm text-slate-600">Utilize pelo menos 8 caracteres e evite reutilizar palavras-passe.</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 p-6 sm:p-8">
              <PasswordField
                id="current-password-input"
                label="Palavra-passe atual"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                visible={visibleFields.current}
                onToggle={() => toggleVisibility('current')}
                autoComplete="current-password"
              />
              <div className="grid gap-5 md:grid-cols-2">
                <PasswordField
                  id="new-password-input"
                  label="Nova palavra-passe"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  visible={visibleFields.next}
                  onToggle={() => toggleVisibility('next')}
                  autoComplete="new-password"
                />
                <PasswordField
                  id="repeat-password-input"
                  label="Confirmar nova palavra-passe"
                  value={repeatPassword}
                  onChange={(event) => setRepeatPassword(event.target.value)}
                  visible={visibleFields.repeat}
                  onToggle={() => toggleVisibility('repeat')}
                  autoComplete="new-password"
                />
              </div>

              {feedback && (
                <div
                  className={`flex items-start gap-3 rounded-2xl border p-4 text-sm ${feedback.type === 'success'
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                    : 'border-red-200 bg-red-50 text-red-800'}`}
                  role={feedback.type === 'error' ? 'alert' : 'status'}
                >
                  {feedback.type === 'success'
                    ? <CheckCircleIcon className="h-5 w-5 shrink-0" />
                    : <ExclamationCircleIcon className="h-5 w-5 shrink-0" />}
                  <p className="font-medium">{feedback.message}</p>
                </div>
              )}

              <div className="flex justify-end border-t border-slate-100 pt-6">
                <button
                  type="submit"
                  disabled={isSubmitting || !currentPassword || !newPassword || !repeatPassword}
                  className="inline-flex min-w-48 items-center justify-center rounded-xl bg-green-cl px-5 py-3 font-semibold text-white shadow-sm transition hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSubmitting ? 'A guardar...' : 'Alterar palavra-passe'}
                </button>
              </div>
            </form>
            </section>

            <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
              <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-6 sm:px-8">
                <div className="flex items-start gap-4">
                  <span className="rounded-2xl bg-emerald-50 p-3 text-emerald-700">
                    <FingerPrintIcon className="h-6 w-6" />
                  </span>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-950">Credenciais da API Eupago</h2>
                    <p className="mt-1 text-sm text-slate-600">
                      Estas credenciais serão utilizadas para gerir as integrações de todos os seus formulários.
                    </p>
                  </div>
                </div>
                <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
                  credentialsConfigured
                    ? 'bg-emerald-100 text-emerald-800'
                    : 'bg-amber-100 text-amber-800'
                }`}>
                  {credentialsConfigured ? 'Configuradas' : 'Por configurar'}
                </span>
              </div>

              <form onSubmit={handleCredentialsSubmit} className="space-y-5 p-6 sm:p-8">
                {credentialsLoading ? (
                  <div className="flex items-center gap-3 text-sm text-slate-600">
                    <span className="h-5 w-5 animate-spin rounded-full border-2 border-emerald-700 border-t-transparent" />
                    A carregar credenciais...
                  </div>
                ) : (
                  <>
                    <div>
                      <label htmlFor="eupago-client-id" className="mb-2 block text-sm font-semibold text-slate-800">
                        Client ID
                      </label>
                      <input
                        id="eupago-client-id"
                        type="text"
                        value={credentialClientId}
                        onChange={(event) => setCredentialClientId(event.target.value)}
                        autoComplete="off"
                        required
                        className="block w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-100"
                      />
                    </div>

                    <div className="grid gap-5 md:grid-cols-2">
                      <PasswordField
                        id="eupago-client-secret"
                        label={credentialsConfigured ? 'Novo Client Secret' : 'Client Secret'}
                        value={credentialClientSecret}
                        onChange={(event) => setCredentialClientSecret(event.target.value)}
                        visible={visibleFields.credentialsSecret}
                        onToggle={() => toggleVisibility('credentialsSecret')}
                        autoComplete="new-password"
                      />
                      <PasswordField
                        id="credentials-current-password"
                        label="Palavra-passe atual"
                        value={credentialPassword}
                        onChange={(event) => setCredentialPassword(event.target.value)}
                        visible={visibleFields.credentialsPassword}
                        onToggle={() => toggleVisibility('credentialsPassword')}
                        autoComplete="current-password"
                      />
                    </div>

                    <p className="text-xs leading-5 text-slate-500">
                      Por segurança, o Client Secret guardado nunca é apresentado. Introduza um novo valor sempre que
                      precisar de atualizar as credenciais.
                    </p>

                    {credentialsFeedback && (
                      <div
                        className={`flex items-start gap-3 rounded-2xl border p-4 text-sm ${
                          credentialsFeedback.type === 'success'
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                            : 'border-red-200 bg-red-50 text-red-800'
                        }`}
                        role={credentialsFeedback.type === 'error' ? 'alert' : 'status'}
                      >
                        {credentialsFeedback.type === 'success'
                          ? <CheckCircleIcon className="h-5 w-5 shrink-0" />
                          : <ExclamationCircleIcon className="h-5 w-5 shrink-0" />}
                        <p className="font-medium">{credentialsFeedback.message}</p>
                      </div>
                    )}

                    <div className="flex justify-end border-t border-slate-100 pt-6">
                      <button
                        type="submit"
                        disabled={
                          credentialsSubmitting
                          || !credentialClientId.trim()
                          || !credentialClientSecret
                          || !credentialPassword
                        }
                        className="inline-flex min-w-48 items-center justify-center rounded-xl bg-green-cl px-5 py-3 font-semibold text-white shadow-sm transition hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {credentialsSubmitting ? 'A guardar...' : 'Guardar credenciais'}
                      </button>
                    </div>
                  </>
                )}
              </form>
            </section>
          </div>
        </div>
      </div>
    </main>
  );
};

export default UserPage;
